import { useState, useEffect, useCallback } from "react";
import {
	getUserData,
	persistUserData,
	clearUserData,
	USER_DATA_UPDATED_EVENT,
} from "../utils/userStorage";
import { SESSION_EXPIRED_EVENT } from "../utils/sessionAuth";

export const AUTH_TOKEN_UPDATED_EVENT = "auth-token-updated";

function readStoredToken() {
	try {
		const raw = localStorage.getItem("jwt_token");
		if (!raw || raw === "null") return null;
		return JSON.parse(raw);
	} catch {
		return null;
	}
}

function useAuthentication() {
	const getInfoFromLocalStorage = (localStoragaItem) => {
		const data = JSON.parse(localStorage.getItem(localStoragaItem));
		if (!data) return null;
		return data;
	};

	const saveItemInLocalStorage = (itemName, newItem) => {
		const stringItemInfo = JSON.stringify(newItem);
		localStorage.setItem(itemName, stringItemInfo);
	};

	const deleteItemFromLocalStorage = (itemName) => {
		localStorage.removeItem(itemName);
		if (itemName === "user_data") {
			window.dispatchEvent(
				new CustomEvent(USER_DATA_UPDATED_EVENT, { detail: {} }),
			);
		}
	};

	const [currentToken, setCurrentTokenState] = useState(readStoredToken);
	const [userInfo, setUserInfo] = useState(() => getUserData() || {});

	const setCurrentToken = useCallback((token) => {
		setCurrentTokenState(token);
		window.dispatchEvent(new CustomEvent(AUTH_TOKEN_UPDATED_EVENT, { detail: { token } }));
	}, []);

	useEffect(() => {
		const handleUserDataUpdated = (event) => {
			const detail = event.detail;
			if (detail && typeof detail === "object" && "id" in detail) {
				setUserInfo(detail);
				return;
			}
			setUserInfo(getUserData() || {});
		};

		const handleSessionExpired = () => {
			setCurrentTokenState(null);
			setUserInfo({});
		};

		const handleTokenUpdated = (event) => {
			setCurrentTokenState(event.detail?.token ?? null);
		};

		window.addEventListener(USER_DATA_UPDATED_EVENT, handleUserDataUpdated);
		window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
		window.addEventListener(AUTH_TOKEN_UPDATED_EVENT, handleTokenUpdated);
		return () => {
			window.removeEventListener(USER_DATA_UPDATED_EVENT, handleUserDataUpdated);
			window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
			window.removeEventListener(AUTH_TOKEN_UPDATED_EVENT, handleTokenUpdated);
		};
	}, []);

	const updateUserInfo = useCallback((partialOrFull, options) => {
		const next = persistUserData(partialOrFull, options);
		setUserInfo(next || {});
		return next;
	}, []);

	const resetUserInfo = useCallback(() => {
		clearUserData();
		setUserInfo({});
	}, []);

	return {
		setCurrentToken,
		currentToken,
		userInfo,
		setUserInfo,
		updateUserInfo,
		resetUserInfo,
		saveItemInLocalStorage,
		deleteItemFromLocalStorage,
		getInfoFromLocalStorage,
	};
}

export { useAuthentication };
